import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Alert, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../../tasks/tasklist.css';
import '../../tasks/addtask.css';
import {
  CREDIT_TOPUP_OPEN_EVENT,
  previewCreditTopup,
  getCreditSettings,
  creditCheckoutPath,
  saveCreditCheckoutCredits,
} from '../../../services/CreditService';

export default function CreditTopUpModal() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [credits, setCredits] = useState('');
  const [preview, setPreview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const data = await getCreditSettings();
      if (data?.success) setSettings(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const onOpen = () => {
      setCredits('');
      setPreview(null);
      setShow(true);
      loadSettings();
    };
    window.addEventListener(CREDIT_TOPUP_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CREDIT_TOPUP_OPEN_EVENT, onOpen);
  }, [loadSettings]);

  useEffect(() => {
    if (!show) return undefined;
    const amount = Number(credits);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPreview(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoadingPreview(true);
      try {
        const data = await previewCreditTopup(amount);
        if (data?.success) {
          setPreview(data);
        } else {
          setPreview({ error: data?.message, ...data });
        }
      } catch (err) {
        setPreview({ error: err.response?.data?.message || 'Failed to preview top-up' });
      } finally {
        setLoadingPreview(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [show, credits]);

  const handleClose = () => {
    if (!submitting) setShow(false);
  };

  const minCredits = settings?.minimum_credits ?? 1;

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = Number(credits);
    if (!Number.isFinite(amount) || amount < minCredits) {
      toast.error(`Minimum credits to purchase is ${minCredits}`);
      return;
    }
    if (preview?.error || !preview?.amount_usd) {
      return;
    }

    setSubmitting(true);
    saveCreditCheckoutCredits(amount);
    setShow(false);
    navigate(creditCheckoutPath(amount));
    setSubmitting(false);
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Top up credits</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Credits to purchase in USD</Form.Label>
            <Form.Control
              type="number"
              min={minCredits}
              step="0.01"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              required
            />
          </Form.Group>

          {preview?.error && (
            <Alert variant="danger" className="mb-0 mt-2">
              {preview.error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="task-form-toolbar toolbar toolbar-bottom border-0 pt-0">
          <button
            type="button"
            className="btn btn-cancel task-form-toolbar-btn"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="new-user-btn task-form-toolbar-btn"
            disabled={submitting || loadingPreview || !!preview?.error || !preview?.amount_usd}
          >
            Continue to checkout
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
